import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  isCustomRange,
  type PresetRange,
  TIME_RANGES,
  type TimeRangeMode,
} from "./DashboardDefinition";

dayjs.extend(utc);

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * An absolute, resolved time window — what every metrics consumer works with.
 *
 * DERIVED, NEVER PERSISTED. Storing it would freeze a preset's drift, so a
 * saved "last 30 days" would stop meaning that. {@link TimeRangeMode} is the
 * persisted shape; this is what {@link TimeRange.fromMode} turns it into.
 *
 * Milliseconds, not nanoseconds: epoch-ms sits ~5000x UNDER
 * `Number.MAX_SAFE_INTEGER` and is exact as a plain `number`, where epoch-ns
 * is ~198x over and quantizes to 256ns steps. Nanoseconds are a wire format
 * and appear only where the request is serialized.
 *
 * `end_ms` is EXCLUSIVE. That keeps `aggregate_metric` from widening the
 * window — an exclusive midnight already sits on a bucket boundary — and it
 * makes duration arithmetic plain subtraction.
 */
export class TimeRange {
  readonly start_ms: number;
  /** Exclusive. */
  readonly end_ms: number;

  private constructor(start_ms: number, end_ms: number) {
    if (!Number.isFinite(start_ms) || !Number.isFinite(end_ms)) {
      throw new Error("TimeRange bounds must be finite epoch milliseconds");
    }
    // Only reachable through a programming error: `asTimeRange` already
    // rejects a reversed custom range at the untrusted-input boundary.
    if (start_ms > end_ms) {
      throw new Error(`TimeRange start ${start_ms} is after end ${end_ms}`);
    }
    this.start_ms = start_ms;
    this.end_ms = end_ms;
  }

  /**
   * The single place the clock is read. Resolve once and share the instance —
   * every consumer then shows the same window, and a memo keyed on it stays
   * stable.
   */
  static fromMode(mode: TimeRangeMode, now: Date = new Date()): TimeRange {
    return isCustomRange(mode)
      ? TimeRange.fromDates(mode.start, mode.end)
      : TimeRange.fromPreset(mode, now);
  }

  /**
   * `"30d"` → `[now - 30d, now)`, with `now` floored to the minute. Separate
   * from `fromMode` so a preset's range is testable against a fixed `now`
   * without a component.
   *
   * Two calls within the same minute return equal ranges, so query cache
   * keys built from the range bounds match and the later fetch is served from
   * the cache. The range's end is up to 59.999 seconds before `now`, so data
   * from `now`'s minute is left out of the range.
   */
  static fromPreset(preset: PresetRange, now: Date = new Date()): TimeRange {
    const days =
      TIME_RANGES.find((tr) => tr.value === preset)?.days ??
      TIME_RANGES[0].days;
    const end = Math.floor(now.getTime() / MS_PER_MINUTE) * MS_PER_MINUTE;
    return new TimeRange(end - days * MS_PER_DAY, end);
  }

  /**
   * Two INCLUSIVE `YYYY-MM-DD` dates → `[start 00:00Z, end+1d 00:00Z)`.
   *
   * Owns the `+1 day`, so no caller has to remember it. Sending the inclusive
   * end instead would silently drop the final day's data.
   */
  static fromDates(startIso: string, endIso: string): TimeRange {
    const start = dayjs.utc(startIso, "YYYY-MM-DD", true);
    const end = dayjs.utc(endIso, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid()) {
      throw new Error(
        `TimeRange needs YYYY-MM-DD dates, got ${startIso}/${endIso}`,
      );
    }
    return new TimeRange(start.valueOf(), end.add(1, "day").valueOf());
  }

  /** Primitive escape hatch — the factory the others build on, and what tests
   *  use to construct a window without a clock. */
  static fromMs(start_ms: number, end_ms: number): TimeRange {
    return new TimeRange(start_ms, end_ms);
  }

  get durationMs(): number {
    return this.end_ms - this.start_ms;
  }

  /** Feeds `periodForDuration`. Jan 5 through Jan 20 inclusive reads as 16. */
  get durationDays(): number {
    return this.durationMs / MS_PER_DAY;
  }

  /**
   * Back to two INCLUSIVE dates, for seeding the picker.
   *
   * The end is the date holding the last instant the window covers, found by
   * stepping back 1ms from the exclusive end. Subtracting a whole day instead
   * would only be right for a window built by {@link TimeRange.fromDates},
   * whose end is midnight; a preset ends at `now`, mid-day, and would report
   * the previous date.
   */
  toDates(): { start: string; end: string } {
    return {
      start: dayjs.utc(this.start_ms).format("YYYY-MM-DD"),
      end: dayjs.utc(this.end_ms - 1).format("YYYY-MM-DD"),
    };
  }

  /** Instances are fresh objects, so `===` is never meaningful. Compare with
   *  this rather than letting a memo fall back to identity. */
  equals(other: TimeRange): boolean {
    return this.start_ms === other.start_ms && this.end_ms === other.end_ms;
  }
}
